import {useCallback, useEffect, useRef, useState} from "react";
import ServiceClient from "../../utils/ServiceClient";
import {
  buildSafePaysafeTokenizationErrorLog, getUnsupportedCardBrandMessage, isPaysafeConfigured, mountPaysafeCardFields,
  paysafeTokenizationPreparationErrorMessage, tokenizePaysafeCard, type PaysafeBillingAddress,
  type PaysafeCardFieldHostIds, type PaysafeFieldsInstance
} from "../../services/paysafeTokenization";
import {PaysafeInstrumentType} from "../../types";
import {colors} from "./theme";
import {buildSafeWithdrawFlowErrorLog} from "./utils";

/**
 * Drives Paysafe hosted-field card entry for the withdraw flow (SMG-4057). A one-to-one sibling of
 * {@code useFinixCardEntry}: Paysafe owns the inputs (three cross-origin iframes), this hook mounts
 * them, tokenizes on save and registers the result with paygate. The returned {@code paysafe:<id>}
 * handle is the payout destination; paygate re-validates ownership server-side.
 *
 * <p>Only offered when the server asks for it ({@code cardEntryProvider === 'PAYSAFE'}) AND this
 * build carries the Paysafe public key — the caller combines the first with {@code isSupported}.</p>
 */

// Provider-neutral on purpose (PS-17): these ids land in the customer's DOM.
const fieldHostIds: PaysafeCardFieldHostIds = {
  cardNumber: 'payout-card-number', expiryDate: 'payout-card-expiry', cvv: 'payout-card-cvv'
};

// Paysafe styles the inputs inside its iframes from this object; our own styles cannot reach them.
// The app font is not available inside the iframe, so fall back to a system stack.
const fieldStyle = {
  input: {color: colors.textPrimary, 'font-size': '14px', 'font-family': 'Helvetica, Arial, sans-serif'}
};

const paysafeSaveErrorMessage = 'We could not save this card. Please check the details and try again.';

const paysafeUnprovisionedCardMessage = 'This card cannot receive instant payouts. Try a different card.';

// Card-save refusals the customer can act on (critic M5). Generic on purpose (PS-17).
// 409 is a refusal, not a duplicate: paygate answers it when Paysafe already holds the card but the
// profile is ambiguous, so the card is NOT saved and "already saved" would send the user to a list
// that does not have it.
const saveRefusalStatusMessages = new Map<number, string>([
  [400, paysafeSaveErrorMessage],
  [409, "We couldn't save this card. Please try again later or contact support."],
  [429, 'Too many attempts to add a card. Please try again tomorrow.'],
  [503, 'Adding a card is temporarily unavailable. Please try again later.']
]);
// 422 reasons with their own copy.
const saveRefusalReasonMessages = new Map<string, string>([
  ['KYC_REQUIRED', 'Please finish verifying your identity before adding a payout card.'],
  ['NO_PAYOUT_INSTRUMENT', paysafeUnprovisionedCardMessage]
]);
// 422 reasons that mean "not this provider for this user": the default card entry takes over.
const providerUnavailableReasons = ['REGION_NOT_SUPPORTED', 'COMING_SOON'];

type Status = 'idle' | 'loading' | 'ready' | 'error';

/**
 * @param onProviderUnavailable called when the card save says this provider is not available to the
 *   user (422 REGION_NOT_SUPPORTED / COMING_SOON); the caller switches to its default card entry.
 */
export const usePaysafeCardEntry = (isActive: boolean, onProviderUnavailable?: () => void) => {
  const isSupported = isPaysafeConfigured();
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const instanceRef = useRef<PaysafeFieldsInstance>();
  const unmountRef = useRef<() => void>();
  // Bumped on every (re)mount so fields torn down mid-flight cannot resolve into the state of a
  // later mount — the same staleness guard useFinixCardEntry applies.
  const mountIdRef = useRef(0);

  const teardown = useCallback(() => {
    mountIdRef.current += 1;
    instanceRef.current = undefined;
    try {
      unmountRef.current?.();
    } catch {
      // The host elements are already gone (unmount race); nothing to detach.
    }
    unmountRef.current = undefined;
  }, []);

  const mount = useCallback(() => {
    if (!isSupported) return;

    const mountId = ++mountIdRef.current;
    setStatus('loading');
    setErrorMessage('');

    // The hosts must exist before Paysafe resolves them by selector, and they are rendered by the
    // step this hook serves — so mounting waits a frame rather than racing React's commit.
    const attempt = () => mountPaysafeCardFields(fieldHostIds, fieldStyle).then(({instance, unmount}) => {
      if (mountId !== mountIdRef.current) {
        unmount();
        return;
      }
      instanceRef.current = instance;
      unmountRef.current = unmount;
      setStatus('ready');
    }).catch(error => {
      if (mountId !== mountIdRef.current) return;
      console.log('hosted card fields mount error', buildSafePaysafeTokenizationErrorLog(error));
      setStatus('error');
      setErrorMessage(paysafeTokenizationPreparationErrorMessage);
    });

    const frame = setTimeout(attempt, 0);
    return () => clearTimeout(frame);
  }, [isSupported]);

  useEffect(() => {
    if (!isActive || !isSupported) {
      teardown();
      setStatus('idle');
      // Per-attempt state dies with the attempt: re-entering the step must not greet the user with
      // a stale "could not save this card" from the previous one.
      setIsSaving(false);
      setFormError('');
      return;
    }
    const cancelPendingMount = mount();
    return () => {
      cancelPendingMount?.();
      teardown();
    };
  }, [isActive, isSupported, mount, teardown]);

  const retry = useCallback(() => {
    teardown();
    mount();
  }, [mount, teardown]);

  /**
   * Tokenize with Paysafe and register the result as a payout destination. Resolves the provisioned
   * instrument, or null when the attempt failed (the message is on {@code formError}) or the server
   * handed card entry back to the default provider ({@code onProviderUnavailable}).
   */
  const saveCard = useCallback(async (billingAddress?: PaysafeBillingAddress): Promise<PaysafeInstrumentType | null> => {
    const instance = instanceRef.current;
    if (isSaving || status !== 'ready' || !instance) {
      setFormError(errorMessage || paysafeTokenizationPreparationErrorMessage);
      return null;
    }

    const mountId = mountIdRef.current;
    setIsSaving(true);
    setFormError('');

    try {
      const {token} = await tokenizePaysafeCard(instance, billingAddress);
      // The fields can be torn down while Paysafe is still tokenizing; a token that arrives after
      // that belongs to a screen the user has left, so it must not update this one.
      if (mountId !== mountIdRef.current) return null;

      const payGateToken = await ServiceClient.getPayGateToken();
      if (mountId !== mountIdRef.current) return null;

      const instrument = await ServiceClient.savePaysafeInstrument(token, payGateToken);
      if (mountId !== mountIdRef.current) return null;

      if (!instrument.provisioned) {
        setFormError(paysafeUnprovisionedCardMessage);
        return null;
      }
      // Without a ref there is nothing to pay to: report it now rather than leave the user on a
      // list with no new card and no message.
      if (!instrument.destinationRef) {
        setFormError(paysafeSaveErrorMessage);
        return null;
      }

      return instrument;
    } catch (error) {
      if (mountId !== mountIdRef.current) return null;
      // HTTP status + reason code only, never the error itself: it carries the request, and the token.
      const {status, reason} = buildSafeWithdrawFlowErrorLog(error);
      console.log('hosted card fields saveCard error', {...buildSafePaysafeTokenizationErrorLog(error), status, reason});

      const refusalReason = status === 422 && typeof reason === 'string' ? reason : '';
      if (onProviderUnavailable && providerUnavailableReasons.includes(refusalReason)) {
        onProviderUnavailable();
        return null;
      }
      // A brand the payout account cannot take (SMG-4797) has its own copy: trying again will not help.
      setFormError(
        getUnsupportedCardBrandMessage(error) || saveRefusalReasonMessages.get(refusalReason)
          || saveRefusalStatusMessages.get(status ?? 0) || paysafeSaveErrorMessage
      );
      return null;
    } finally {
      // Unconditionally, never behind the mount-id guard — see useFinixCardEntry: gating it left
      // isSaving stuck true after cancelling mid-save, and the modal is never unmounted.
      setIsSaving(false);
    }
  }, [errorMessage, isSaving, onProviderUnavailable, status]);

  return {isSupported, fieldHostIds, status, errorMessage, formError, isSaving, retry, saveCard};
};
